import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { OfferViewModel } from '../models/com/offer.model';
import { MOCK_OFFERS_RESPONSE } from '../shared/mocks/offers.mock';
import { SITES_QUERY } from '../shared/graphql/offers.query';
import { ApiEndpointsService } from './api-endpoints.service';
import { Site, SitesQueryResponse } from '../models/com/site.model';

@Injectable({
  providedIn: 'root',
})
export class OffersService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiEndpointsService);

  getLatestOffers(args?: { limit: number }): Observable<OfferViewModel[]> {
    // Uncomment for mock data
    // const data = MOCK_OFFERS_RESPONSE as Site[];
    // return of(this.transformSitesToDisplayOffers(data, args?.limit));

    return this.getSites().pipe(
      map((res) => {
        //map the sites to OfferViewModel
        const transformed = this.transformSitesToDisplayOffers(res?.data.sites, args?.limit);
        if (args?.limit) {
          return transformed.slice(0, args.limit);
        }
        return transformed;
      }),
      catchError((err) => {
        console.error('Failed to load latest Offers', err);
        return of<OfferViewModel[]>([]);
      })
    );
  }

  getSites(): Observable<SitesQueryResponse> {
    const endpoint = this.apiService.baseUrl;
    return this.http.post<SitesQueryResponse>(endpoint, { query: SITES_QUERY }, { withCredentials: true });
  }

  getSitesWithDemand(): Observable<SitesQueryResponse> {
    const endpoint = this.apiService.baseUrl;
    return this.http.post<SitesQueryResponse>(endpoint, { query: SITES_QUERY }, { withCredentials: true });
  }

  /**
   * Transforms a nested structure of sites, registrations, and offers into a
   * single, flat list of displayable offer objects.
   */
  private transformSitesToDisplayOffers(sites: Site[] | null, max?: number): OfferViewModel[] {
    // Return an empty array immediately if sites is null or empty.
    if (!sites) {
      return [];
    }

    const offersByRegistrantId = new Map<string, OfferViewModel[]>();

    const transformedOffers = sites.flatMap((site) =>
      (site.registrations ?? []).flatMap((registration) =>
        // For each registration was only one Program associated
        {
          /* For each registration was only one Program associated*/
          return (registration.offers ?? []).flatMap((offer) => {
            if (!offer.program) {
              return [];
            }
            // If it passes, return a single-item array containing the transformed object.
            const offerViewModel: OfferViewModel = {
              siteName: site.display_labels ?? [],
              //TODO: Remove site details column as per new design
              siteDescription: registration?.display_labels ?? [],
              offerStartDttmUtc: offer.offer_start_dttm_utc,
              offerEndDttmUtc: offer.offer_end_dttm_utc,
              offerValue: offer.offer_value,
              programName: offer.program.display_labels,
              defaultLocale: offer.program.default_locale,
            };
            if (!offersByRegistrantId.has(registration.id)) {
              offersByRegistrantId.set(registration.id, []);
            }
            offersByRegistrantId.get(registration.id)?.push(offerViewModel);
            return [offerViewModel];
          });
        }
      )
    );

    // If no max is provided, or if the total number of offers is within the limit, return all offers sorted by date.
    if (!max || transformedOffers.length <= max) {
      return transformedOffers.sort(this.dateComparator);
    }

    for (const offers of offersByRegistrantId.values()) {
      offers.sort(this.dateComparator);
    }
    const limitedOffers = Array.from(offersByRegistrantId.values()).flatMap((offers) => offers.slice(0, 1));

    // If the number of unique registrations is greater than or equal to the max, we can only include one offer per registration
    if (offersByRegistrantId.size >= max) {
      return limitedOffers.sort(this.dateComparator).slice(0, max);
    }

    const remainingSlots = max - offersByRegistrantId.size;
    transformedOffers.sort(this.dateComparator);

    const filteredTransformedOffers = transformedOffers.filter((offer) => !limitedOffers.includes(offer)).slice(0, remainingSlots);

    return [...limitedOffers, ...filteredTransformedOffers].sort(this.dateComparator).slice(0, max);
  }

  private dateComparator(a: OfferViewModel, b: OfferViewModel): number {
    return new Date(a.offerStartDttmUtc).getTime() - new Date(b.offerStartDttmUtc).getTime();
  }
}
